import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import {
  Building2, Loader2, Plus, CheckCircle2, XCircle, Clock,
  ChevronRight, ArrowLeft, FileText, AlertTriangle, Shield,
  Upload, Users, BarChart3, Search
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

  const STATUS_STYLES = {
  draft: { bg: "bg-slate-400/10", text: "text-slate-400", label: "Draft" },
  submitted: { bg: "bg-blue-400/10", text: "text-blue-400", label: "Submitted" },
  under_review: { bg: "bg-amber-400/10", text: "text-amber-400", label: "Under Review" },
  approved: { bg: "bg-emerald-400/10", text: "text-emerald-400", label: "Approved" },
  rejected: { bg: "bg-red-400/10", text: "text-red-400", label: "Rejected" },
  inactive: { bg: "bg-slate-500/10", text: "text-slate-500", label: "Inactive" },
};

const TYPE_LABELS = {
  supplier: "Supplier",
  tpm: "Third-Party Manufacturer",
  service_provider: "Service Provider",
  consultant_lab: "Consultant / Lab",
};

export default function VendorRegistrationPage() {
  const { t } = useTranslation('operations');
  const { user } = useAuth();
  const [tab, setTab] = useState("list");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showDoc, setShowDoc] = useState(false);
  const [showRisk, setShowRisk] = useState(false);
  const [filter, setFilter] = useState("");

  const loadVendors = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/vendors?limit=100`);
      setVendors(res.data.vendors || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadVendors().then(() => setLoading(false));
  }, [loadVendors]);

  const loadVendorDetail = async (vendorId) => {
    try {
      const res = await ax.get(`${API}/vendors/${vendorId}`);
      setSelectedVendor(res.data);
    } catch {}
  };

  const registerVendor = async (data) => {
    try {
      const res = await ax.post(`${API}/vendors/register`, data);
      setShowNew(false);
      loadVendors();
      loadVendorDetail(res.data.vendor_id);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to register vendor");
    }
  };

  const submitVendor = async (vendorId) => {
    try {
      await ax.post(`${API}/vendors/${vendorId}/submit`);
      loadVendorDetail(vendorId);
      loadVendors();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit vendor");
    }
  };

  const approveVendor = async (vendorId) => {
    if (!window.confirm("Approve this vendor?")) return;
    try {
      await ax.post(`${API}/vendors/${vendorId}/approve`, { notes: "Approved after review" });
      loadVendorDetail(vendorId);
      loadVendors();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to approve vendor");
    }
  };

  const rejectVendor = async (vendorId) => {
    const reason = prompt("Rejection reason:");
    if (!reason) return;
    try {
      await ax.post(`${API}/vendors/${vendorId}/reject`, { reason });
      loadVendorDetail(vendorId);
      loadVendors();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to reject vendor");
    }
  };

  const uploadDoc = async (vendorId, data) => {
    try {
      await ax.post(`${API}/vendors/${vendorId}/documents`, data);
      setShowDoc(false);
      loadVendorDetail(vendorId);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to upload document");
    }
  };

  const createRiskProfile = async (vendorId, data) => {
    try {
      await ax.post(`${API}/vendors/${vendorId}/risk-profile`, data);
      setShowRisk(false);
      loadVendorDetail(vendorId);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create risk profile");
    }
  };

  if (loading) {
    return (
      <QMSLayout title={t('vendor.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  // Vendor Detail
  if (selectedVendor) {
    const st = STATUS_STYLES[selectedVendor.status] || STATUS_STYLES.draft;
    return (
      <QMSLayout title={t('operations:vendor.title')}>
        <div className="space-y-4" data-testid="vendor-detail">
          <button onClick={() => { setSelectedVendor(null); loadVendors(); }} className="flex items-center gap-2 text-[#8B95A5] hover:text-white text-sm" data-testid="back-from-vendor">
            <ArrowLeft className="w-4 h-4" /> {t('vendor.backToVendors')}
          </button>

          {/* Header */}
          <div className="bg-[#181B23] rounded-lg p-5 border border-[#2A2D38]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-white font-bold text-lg">{selectedVendor.name}</h2>
                <div className="text-xs text-[#8B95A5] mt-1">{selectedVendor.vendor_id} &middot; {TYPE_LABELS[selectedVendor.vendor_type] || selectedVendor.vendor_type}</div>
              </div>
              <span className={`px-3 py-1 rounded text-xs ${st.bg} ${st.text}`} data-testid="vendor-status-badge">{st.label}</span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div><span className="text-[#8B95A5]">Industry:</span> <span className="text-white ml-2">{selectedVendor.industry || "—"}</span></div>
              <div><span className="text-[#8B95A5]">Country:</span> <span className="text-white ml-2">{selectedVendor.country || "—"}</span></div>
              <div><span className="text-[#8B95A5]">Contact:</span> <span className="text-white ml-2">{selectedVendor.primary_contact_name || "—"}</span></div>
              <div><span className="text-[#8B95A5]">Email:</span> <span className="text-white ml-2">{selectedVendor.primary_contact_email || "—"}</span></div>
              {selectedVendor.website && <div><span className="text-[#8B95A5]">Website:</span> <span className="text-white ml-2">{selectedVendor.website}</span></div>}
            </div>

            {selectedVendor.description && <p className="text-sm text-[#8B95A5] mb-4">{selectedVendor.description}</p>}

            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
              {selectedVendor.status === "draft" && (
                <button onClick={() => submitVendor(selectedVendor.vendor_id)} className="px-3 py-2 rounded bg-blue-500/20 text-blue-400 text-sm hover:bg-blue-500/30" data-testid="submit-vendor-btn">{t('vendor.submitForReview')}</button>
              )}
              {["submitted", "under_review"].includes(selectedVendor.status) && user?.role === "admin" && (
                <>
                  <button onClick={() => approveVendor(selectedVendor.vendor_id)} className="px-3 py-2 rounded bg-emerald-500/20 text-emerald-400 text-sm hover:bg-emerald-500/30 flex items-center gap-2" data-testid="approve-vendor-btn">
                    <CheckCircle2 className="w-4 h-4" /> {t('vendor.approve')}
                  </button>
                  <button onClick={() => rejectVendor(selectedVendor.vendor_id)} className="px-3 py-2 rounded bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 flex items-center gap-2" data-testid="reject-vendor-btn">
                    <XCircle className="w-4 h-4" /> {t('vendor.reject')}
                  </button>
                </>
              )}
              <button onClick={() => setShowDoc(true)} className="px-3 py-2 rounded bg-[#1F222D] text-[#8B95A5] text-sm hover:bg-[#2A2D38] flex items-center gap-2" data-testid="upload-doc-btn">
                <Upload className="w-4 h-4" /> {t('vendor.uploadDocument')}
              </button>
              <button onClick={() => setShowRisk(true)} className="px-3 py-2 rounded bg-[#1F222D] text-[#8B95A5] text-sm hover:bg-[#2A2D38] flex items-center gap-2" data-testid="create-risk-btn">
                <BarChart3 className="w-4 h-4" /> {t('vendor.addRiskProfile')}
              </button>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38]" data-testid="vendor-documents">
            <h3 className="text-white font-semibold text-sm mb-3">{t('vendor.documents')} ({(selectedVendor.documents || []).length})</h3>
            {(selectedVendor.documents || []).length === 0 ? (
              <p className="text-[#8B95A5] text-sm">{t('vendor.noDocuments')}</p>
            ) : (selectedVendor.documents || []).map(d => (
              <div key={d.document_id} className="flex items-center justify-between bg-[#0D0F14] rounded p-3 mb-1">
                <div>
                  <div className="text-white text-sm">{d.title}</div>
                  <div className="text-xs text-[#8B95A5]">{d.document_type} &middot; {d.file_name} {d.required && <span className="text-amber-400 ml-1">(Required)</span>}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${d.status === "uploaded" ? "bg-emerald-400/10 text-emerald-400" : "bg-slate-400/10 text-slate-400"}`}>{d.status}</span>
              </div>
            ))}
          </div>

          {/* Risk Profiles */}
          <div className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38]" data-testid="vendor-risk-profiles">
            <h3 className="text-white font-semibold text-sm mb-3">{t('vendor.riskProfiles')} ({(selectedVendor.risk_profiles || []).length})</h3>
            {(selectedVendor.risk_profiles || []).length === 0 ? (
              <p className="text-[#8B95A5] text-sm">{t('vendor.noRiskProfiles')}</p>
            ) : (selectedVendor.risk_profiles || []).map(r => {
              const riskColor = r.risk_level === "high" ? "text-red-400" : r.risk_level === "medium" ? "text-amber-400" : "text-emerald-400";
              return (
                <div key={r.profile_id} className="bg-[#0D0F14] rounded p-3 mb-1" data-testid={`risk-${r.profile_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`font-medium text-sm ${riskColor}`}>{r.risk_level.toUpperCase()}</span>
                      <span className="text-[#8B95A5] text-sm ml-3">Score: {r.risk_score}/100</span>
                    </div>
                    <span className="text-xs text-[#8B95A5]">by {r.assessed_by_name}</span>
                  </div>
                  {r.mitigation_notes && <p className="text-xs text-[#8B95A5] mt-1">{r.mitigation_notes}</p>}
                  {r.risk_factors?.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {r.risk_factors.map((f, i) => <span key={i} className="text-xs bg-[#1F222D] text-[#8B95A5] px-2 py-0.5 rounded">{f}</span>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Registrations */}
          <div className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38]" data-testid="vendor-registrations">
            <h3 className="text-white font-semibold text-sm mb-3">{t('vendor.registrationHistory')}</h3>
            {(selectedVendor.registrations || []).map(r => {
              const rst = STATUS_STYLES[r.status] || STATUS_STYLES.draft;
              return (
                <div key={r.registration_id} className="bg-[#0D0F14] rounded p-3 mb-1">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm">{r.registration_id}</span>
                    <span className={`px-2 py-1 rounded text-xs ${rst.bg} ${rst.text}`}>{rst.label}</span>
                  </div>
                  {r.review_notes && <p className="text-xs text-[#8B95A5] mt-1">{r.review_notes}</p>}
                </div>
              );
            })}
          </div>

          {showDoc && <DocModal vendorId={selectedVendor.vendor_id} onSubmit={uploadDoc} onClose={() => setShowDoc(false)} />}
          {showRisk && <RiskModal vendorId={selectedVendor.vendor_id} onSubmit={createRiskProfile} onClose={() => setShowRisk(false)} />}
        </div>
      </QMSLayout>
    );
  }

  const filtered = filter ? vendors.filter(v =>
    v.name.toLowerCase().includes(filter.toLowerCase()) || v.vendor_type.includes(filter.toLowerCase())
  ) : vendors;

  return (
    <QMSLayout title={t('operations:vendor.title')}>
      <div className="space-y-4" data-testid="vendor-page">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" />
            <input value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('vendor.searchVendors')}
              className="w-full bg-[#181B23] border border-[#2A2D38] rounded pl-10 pr-4 py-2 text-white text-sm"
              data-testid="vendor-search"
            />
          </div>
          <button onClick={() => setShowNew(true)} className="px-3 py-2 rounded bg-[#36A9E1] text-white text-sm flex items-center gap-2 hover:bg-[#2B8BC2] whitespace-nowrap" data-testid="register-vendor-btn">
            <Plus className="w-4 h-4" /> {t('vendor.registerVendor')}
          </button>
        </div>

        {/* Vendor List */}
        <div className="space-y-2" data-testid="vendors-list">
          {filtered.length === 0 ? (
            <div className="bg-[#181B23] rounded-lg p-8 text-center text-[#8B95A5]">
              <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>{vendors.length === 0 ? t('vendor.noVendors') : t('vendor.noMatch')}</p>
            </div>
          ) : filtered.map(v => {
            const st = STATUS_STYLES[v.status] || STATUS_STYLES.draft;
            return (
              <div key={v.vendor_id} onClick={() => loadVendorDetail(v.vendor_id)}
                className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38] hover:border-[#36A9E1]/30 cursor-pointer"
                data-testid={`vendor-${v.vendor_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium text-sm">{v.name}</div>
                    <div className="text-xs text-[#8B95A5] mt-1">{TYPE_LABELS[v.vendor_type] || v.vendor_type} &middot; {v.industry || "No industry"} &middot; {v.country || "No country"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${st.bg} ${st.text}`}>{st.label}</span>
                    <ChevronRight className="w-4 h-4 text-[#8B95A5]" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {showNew && <NewVendorModal onSubmit={registerVendor} onClose={() => setShowNew(false)} />}
      </div>
    </QMSLayout>
  );
}

function NewVendorModal({ onSubmit, onClose }) {
  const { t } = useTranslation('operations');
  const [name, setName] = useState("");
  const [vendorType, setVendorType] = useState("supplier");
  const [description, setDescription] = useState("");
  const [industry, setIndustry] = useState("");
  const [country, setCountry] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="new-vendor-modal">
      <div className="bg-[#181B23] rounded-xl border border-[#2A2D38] w-full max-w-lg p-6 max-h-[90vh] overflow-auto">
        <h2 className="text-white font-bold text-lg mb-4">Register Vendor</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Vendor Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="vendor-name-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.type')} *</label>
            <select value={vendorType} onChange={e => setVendorType(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="vendor-type-select">
              <option value="supplier">Supplier</option>
              <option value="tpm">Third-Party Manufacturer (TPM)</option>
              <option value="service_provider">Service Provider</option>
              <option value="consultant_lab">Consultant / Lab</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.description')}</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="vendor-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.industry')}</label>
              <input value={industry} onChange={e => setIndustry(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="vendor-industry-input" />
            </div>
            <div>
              <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.country')}</label>
              <input value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="vendor-country-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.primaryContactName')}</label>
              <input value={contactName} onChange={e => setContactName(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="vendor-contact-name-input" />
            </div>
            <div>
              <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.primaryContactEmail')}</label>
              <input value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="vendor-contact-email-input" />
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded bg-[#1F222D] text-[#8B95A5] hover:bg-[#2A2D38] text-sm">{t('vendor.cancel')}</button>
          <button onClick={() => onSubmit({ name, vendor_type: vendorType, description, industry, country, primary_contact_name: contactName, primary_contact_email: contactEmail })}
            disabled={!name}
            className="px-4 py-2 rounded bg-[#36A9E1] text-white text-sm hover:bg-[#2B8BC2] disabled:opacity-40"
            data-testid="register-vendor-submit"
          >{t('vendor.register')}</button>
        </div>
      </div>
    </div>
  );
}

function DocModal({ vendorId, onSubmit, onClose }) {
  const { t } = useTranslation('operations');
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("general");
  const [fileName, setFileName] = useState("");
  const [required, setRequired] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="doc-upload-modal">
      <div className="bg-[#181B23] rounded-xl border border-[#2A2D38] w-full max-w-lg p-6">
        <h2 className="text-white font-bold text-lg mb-4">{t('vendor.uploadDocument')}</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.documentTitle')}</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="doc-title-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.documentType')}</label>
            <select value={docType} onChange={e => setDocType(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="doc-type-select">
              {["general", "certificate", "audit_report", "quality_agreement", "insurance", "license", "sop"].map(item => (
                <option key={item} value={item}>{item.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.fileName')}</label>
            <input value={fileName} onChange={e => setFileName(e.target.value)} placeholder="document.pdf" className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="doc-filename-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#8B95A5]">
            <input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} data-testid="doc-required-check" /> {t('vendor.requiredDocument')}
          </label>
        </div>
        <div className="flex gap-3 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded bg-[#1F222D] text-[#8B95A5] hover:bg-[#2A2D38] text-sm">{t('vendor.cancel')}</button>
          <button onClick={() => onSubmit(vendorId, { title, document_type: docType, file_name: fileName, required })}
            disabled={!title}
            className="px-4 py-2 rounded bg-[#36A9E1] text-white text-sm hover:bg-[#2B8BC2] disabled:opacity-40"
            data-testid="upload-doc-submit"
          >{t('vendor.upload')}</button>
        </div>
      </div>
    </div>
  );
}

function RiskModal({ vendorId, onSubmit, onClose }) {
  const [riskLevel, setRiskLevel] = useState("medium");
  const [riskScore, setRiskScore] = useState(50);
  const [notes, setNotes] = useState("");
  const [factors, setFactors] = useState("");

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="risk-profile-modal">
      <div className="bg-[#181B23] rounded-xl border border-[#2A2D38] w-full max-w-lg p-6">
        <h2 className="text-white font-bold text-lg mb-4">{t('vendor.createRiskProfile')}</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.riskLevel')}</label>
            <select value={riskLevel} onChange={e => setRiskLevel(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="risk-level-select">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.riskScore')}</label>
            <input type="number" min={0} max={100} value={riskScore} onChange={e => setRiskScore(Number(e.target.value))} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="risk-score-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.riskFactors')}</label>
            <input value={factors} onChange={e => setFactors(e.target.value)} placeholder="e.g. single source, no backup facility" className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="risk-factors-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">{t('vendor.mitigationNotes')}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="risk-notes-input" />
          </div>
        </div>
        <div className="flex gap-3 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded bg-[#1F222D] text-[#8B95A5] hover:bg-[#2A2D38] text-sm">{t('vendor.cancel')}</button>
          <button onClick={() => onSubmit(vendorId, { risk_level: riskLevel, risk_score: riskScore, risk_factors: factors.split(",").map(f => f.trim()).filter(Boolean), mitigation_notes: notes })}
            className="px-4 py-2 rounded bg-[#36A9E1] text-white text-sm hover:bg-[#2B8BC2]"
            data-testid="create-risk-submit"
          >{t('vendor.createProfile')}</button>
        </div>
      </div>
    </div>
  );
}
